import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Filter, X, DollarSign, Clock, Loader2, BarChart3,
  ArrowUpRight, ArrowDownLeft, Lock, CheckCircle2, AlertCircle, FileText, Search,
} from 'lucide-react';
import Pagination, { paginate, PAGE_SIZE } from './Pagination';
import {
  PageHeader, KpiCard, StatusBadge, toneForStatus,
  DataTable, DataTableHead, DataTableBody, DataTH, DataTR, DataTD,
  DataTableEmpty, DensityToggle, useTableDensity,
} from './ui-shared';
import FilterPanel from './ui-shared/FilterPanel';
import DateRangeFilter from './ui-shared/DateRangeFilter';
import ExportMenu from './ui-shared/ExportMenu';
import SortableHeader, { useTableSort } from './SortableHeader';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';
import { useUnreadBadges } from '../context/UnreadBadgeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:   { label: 'Pending',   cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },  tested:    { label: 'Locked',    cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Lock },
  locked:    { label: 'Locked',    cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Lock },
  end:       { label: 'End',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  failed:    { label: 'Failed',    cls: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle },
  start:     { label: 'Start',     cls: 'bg-blue-100 text-blue-700 border-blue-300', icon: Clock },
};
const DIR_META = {
  to_user:   { label: 'In',  cls: 'text-emerald-600', icon: ArrowDownLeft },
  from_user: { label: 'Out', cls: 'text-rose-600',    icon: ArrowUpRight },
};
const PIE_COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const blank = {
  description: '', date_start: '', date_end: '',
  direction: '', status: '', type: '',
  money_op: '', money_value: '', money_value2: '',
  bonus_filter: 'all',
};

// Client-side filter — we already only have own rows from /api/transactions/
function applyClientFilter(rows, f) {
  return rows.filter((t) => {
    if (f.description && !(t.description || '').toLowerCase().includes(f.description.toLowerCase())) return false;
    if (f.date_start && (t.created_at || '') < f.date_start) return false;
    if (f.date_end) {
      const cutoff = f.date_end.includes('T') ? f.date_end : `${f.date_end}T23:59:59`;
      if ((t.created_at || '') > cutoff) return false;
    }
    if (f.direction && t.direction !== f.direction) return false;
    if (f.status && t.status !== f.status) return false;
    if (f.type && t.type !== f.type) return false;
    if (f.bonus_filter === 'hide' && t.is_bonus) return false;
    if (f.bonus_filter === 'only' && !t.is_bonus) return false;

    if (f.money_op && f.money_value !== '' && f.money_value !== null) {
      const a = Number(t.amount || 0);
      const v = Number(f.money_value);
      const v2 = Number(f.money_value2);
      switch (f.money_op) {
        case 'eq':  if (a !== v) return false; break;
        case 'gt':  if (!(a > v)) return false; break;
        case 'gte': if (!(a >= v)) return false; break;
        case 'lt':  if (!(a < v)) return false; break;
        case 'lte': if (!(a <= v)) return false; break;
        case 'between': {
          const lo = Math.min(v, v2), hi = Math.max(v, v2);
          if (!(a >= lo && a <= hi)) return false; break;
        }
        default: break;
      }
    }
    return true;
  });
}

function computeAnalytics(rows) {
  const byMap = (key) => {
    const m = new Map();
    rows.forEach((t) => {
      const k = t[key] || '—';
      const cur = m.get(k) || { count: 0, amount: 0 };
      cur.count++; cur.amount += Number(t.amount || 0);
      m.set(k, cur);
    });
    return Array.from(m.entries()).map(([k, v]) => ({ key: k, ...v }));
  };
  const dailyMap = new Map();
  rows.forEach((t) => {
    const day = (t.created_at || '').slice(0, 10);
    if (!day) return;
    const cur = dailyMap.get(day) || { day, count: 0, amount: 0, in_amount: 0, out_amount: 0 };
    cur.count++; cur.amount += Number(t.amount || 0);
    if (t.direction === 'to_user') cur.in_amount += Number(t.amount || 0);
    if (t.direction === 'from_user') cur.out_amount += Number(t.amount || 0);
    dailyMap.set(day, cur);
  });
  const paid = rows.filter((t) => !t.is_bonus);
  const bonus = rows.filter((t) => t.is_bonus);
  return {
    total_count: rows.length,
    total_amount: rows.reduce((s, t) => s + Number(t.amount || 0), 0),
    paid_total: paid.reduce((s, t) => s + Number(t.amount || 0), 0),
    paid_count: paid.length,
    bonus_total: bonus.reduce((s, t) => s + Number(t.amount || 0), 0),
    bonus_count: bonus.length,
    by_status: byMap('status'),
    by_direction: byMap('direction'),
    by_type: byMap('type'),
    daily: Array.from(dailyMap.values()).sort((a, b) => a.day.localeCompare(b.day)),
  };
}

// Decode HTML entities so &#8364; renders as € on customer side.
function decodeEntities(s) {
  if (!s) return '';
  return String(s)
    .replace(/&#(\d+);/g, (_, c) => String.fromCharCode(Number(c)))
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#039;|&apos;/g, "'");
}

// Pull a payment-provider ref id out of a freeform legacy description.
const PAY_REF_PATTERNS = [
  [/paypal\s+txn\s*id\s*(\S+)/i,                       'paypal',      'PayPal'],
  [/stripe\s+(?:txn\s*id|payment,\s*id)\s*(\S+)/i,      'stripe',     'Stripe'],
  [/Decta\s+id\s*(\S+)/i,                              'decta',       'Decta'],
  [/paymentwall\s+ref\s+id\s*(\S+)/i,                  'paymentwall', 'Paymentwall'],
  [/Interkassa\s+ref\s+id\s*(\S*)/i,                   'interkassa',  'Interkassa'],
];
function parsePayRef(desc) {
  let text = decodeEntities(desc || '').trim().replace(/^new\s+method\s*\|\s*/i, '');
  if (!text) return null;
  for (const [re, kind, label] of PAY_REF_PATTERNS) {
    const m = text.match(re);
    if (m) return { kind, label, ref: m[1] || null };
  }
  if (/welcome\s+bonus/i.test(text)) return { kind: 'bonus', label: 'Welcome bonus', ref: null };
  if (/bonus\s+balance\s+time\s+has\s+expired/i.test(text)) return { kind: 'bonus_expired', label: 'Bonus expired', ref: null };
  return null;
}
const PM_TONE = {
  paypal:        'bg-blue-100 text-blue-700',
  stripe:        'bg-violet-100 text-violet-700',
  decta:         'bg-cyan-100 text-cyan-700',
  paymentwall:   'bg-emerald-100 text-emerald-700',
  interkassa:    'bg-pink-100 text-pink-700',
  admin_custom:  'bg-amber-100 text-amber-700',
  bonus:         'bg-amber-100 text-amber-700',
  bonus_expired: 'bg-slate-200 text-slate-700',
};

// Customer-side description: same set of cases as the admin table but
// without the inspect / debug affordances.
function DescriptionWithOrderLink({ tx, onOpenOrder, tT }) {
  const desc = decodeEntities(tx?.description || '');
  // Translate the dynamic English description templates produced by the
  // backend (order_complete / order_funds_reserved / order_completed_payment).
  // Pattern-matches keep the order number intact while replacing the
  // surrounding text with a localised version. Falls back to the raw
  // backend string when no pattern matches (custom admin notes etc).
  const localiseDesc = (raw) => {
    if (!raw || !tT) return raw;
    let m = /^Order\s+(\S+)\s+—\s+Funds reserved\s*\(postpaid balance used\)\s*$/i.exec(raw);
    if (m) return tT('descriptions.funds_reserved', { order_no: m[1], defaultValue: raw });
    m = /^Order\s+(\S+)\s+completed\s+—\s+Payment processed\s*$/i.exec(raw);
    if (m) return tT('descriptions.payment_processed', { order_no: m[1], defaultValue: raw });
    m = /^Welcome bonus(?::\s*(.+))?\s*$/i.exec(raw);
    if (m) return tT('descriptions.welcome_bonus', { amount: m[1] || '', defaultValue: raw });
    m = /^Order\s+(\S+)\s+refunded\s*$/i.exec(raw);
    if (m) return tT('descriptions.order_refunded', { order_no: m[1], defaultValue: raw });
    return raw;
  };
  // Reconciliation rows — sum it up in one line, raw text in tooltip.
  if (tx?.legacy_source === 'legacy_balance_adjustment') {
    const credit = tx?.direction === 'to_user';
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-slate-200 text-slate-700">
          {tT ? tT('description_badges.adjustment', { defaultValue: 'adjustment' }) : 'adjustment'}
        </span>
        {tT ? tT('descriptions.balance_adjustment', { sign: credit ? '+' : '−', amount: Number(tx?.amount || 0).toFixed(2), defaultValue: `Balance adjustment (${credit ? '+' : '−'}€${Number(tx?.amount || 0).toFixed(2)})` }) : `Balance adjustment (${credit ? '+' : '−'}€${Number(tx?.amount || 0).toFixed(2)})`}
      </span>
    );
  }
  // Welcome bonus / generic bonus — classifier-stamped at import/backfill
  if (tx?.type === "welcome_bonus" || tx?.payment_method_label === "WELCOME BONUS") {
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-amber-200 text-amber-900">
          {tT ? tT('description_badges.welcome_bonus', { defaultValue: 'WELCOME BONUS' }) : 'WELCOME BONUS'}
        </span>
        <span className="text-slate-700 text-[12px]">{localiseDesc(desc) || (tT ? tT('descriptions.welcome_bonus_credit', { defaultValue: 'Welcome bonus credit' }) : 'Welcome bonus credit')}</span>
      </span>
    );
  }
  if (tx?.type === "bonus" || tx?.category === "bonus") {
    const parsed = parsePayRef(desc);
    return (
      <span className="block leading-tight" title={desc}>
        <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700">
          {tx?.payment_method_label || (tT ? tT('description_badges.bonus', { defaultValue: 'BONUS' }) : 'BONUS')}
        </span>
        {parsed?.ref && (
          <code className="text-[10px] text-slate-700 bg-slate-50 border border-slate-200 px-1 rounded mr-1">
            {parsed.ref}
          </code>
        )}
      </span>
    );
  }
  // Legacy debit linked to an order — show clickable № + vehicle.
  if (tx?.legacy_source === 'old_php_admin' && tx?.direction === 'from_user') {
    const los = tx?.linked_order_summary;
    if (los && (los.manufacturer || los.model || los.order_number)) {
      const onum = los.order_number || `#${los.legacy_order_id || ''}`;
      const vehicle = [los.manufacturer, los.model, los.engine].filter(Boolean).join(' · ');
      return (
        <span className="block leading-tight" title={desc}>
          <span className="inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase bg-rose-100 text-rose-700">
            {tT ? tT('description_badges.order_debit', { defaultValue: 'order debit' }) : 'order debit'}
          </span>
          {onum && los.order_id && onOpenOrder ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenOrder(los.order_id); }}
              className="font-bold text-cyan-700 hover:text-cyan-900 hover:underline mr-1"
              data-testid={`usrtx-order-link-${tx.id}`}
            >
              № {onum}
            </button>
          ) : <span className="font-bold mr-1 text-slate-700">№ {onum}</span>}
          <span className="text-slate-700">{vehicle}</span>
          {los.ecu_type && <span className="ml-1 text-slate-500">· {los.ecu_type}</span>}
        </span>
      );
    }
  }
  // Legacy top-up — chip + parsed ref.
  if (tx?.legacy_source === 'old_php_admin' && tx?.direction === 'to_user') {
    const parsed = parsePayRef(desc);
    const pm = (tx?.payment_method || parsed?.kind || '').toLowerCase();
    const labelMap = { paypal: 'PayPal', stripe: 'Stripe', decta: 'Decta',
      paymentwall: 'Paymentwall', interkassa: 'Interkassa',
      admin_custom: 'Admin', bonus: 'Bonus', bonus_expired: 'Bonus expired' };
    const label = labelMap[pm] || (tx?.payment_method || 'Top-up');
    const tone = PM_TONE[pm] || 'bg-slate-200 text-slate-700';
    const ref = parsed?.ref;
    return (
      <span className="block leading-tight" title={desc}>
        <span className={`inline-block px-1 py-0.5 mr-1 rounded text-[9px] font-bold uppercase ${tone}`}>
          {label}
        </span>
        {ref && (
          <code className="text-[10px] text-slate-700 bg-slate-50 border border-slate-200 px-1 rounded mr-1">
            {ref}
          </code>
        )}
        {!ref && (pm === 'admin_custom' || pm === 'bonus' || pm === 'bonus_expired') && (
          <span className="text-slate-600 text-[12px]">{desc}</span>
        )}
      </span>
    );
  }
  // Native order-linked transaction.
  const orderNum = tx?.order_number;
  const localised = localiseDesc(desc);
  if (!orderNum || !onOpenOrder || !tx?.order_id) {
    return <span className="line-clamp-2" title={desc}>{localised}</span>;
  }
  // For the native order-linked rows the localised text already contains
  // the order number (from the {{order_no}} placeholder), so we still want
  // to render it as a clickable link. Split on the literal number.
  const parts = localised.split(orderNum);
  return (
    <span className="line-clamp-2" title={desc}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenOrder(tx.order_id); }}
              className="font-bold text-cyan-700 hover:text-cyan-900 hover:underline"
              data-testid={`usrtx-order-link-${tx.id}`}
            >
              {orderNum}
            </button>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

export default function UserTransactions({ token, onOpenOrder }) {
  const { t } = useTranslation('transactions');
  const { t: tCommon } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  const [filters, setFilters] = useState(blank);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [page, setPage] = useState(1);
  // Client-side sort over the FULL filtered dataset (data is already fully
  // loaded with limit=1000 below, so this still meets the "full dataset
  // sort, not visible-page-only" requirement — filters first, sort
  // second, paginate third).
  const { sortBy, sortDir, onSortChange } = useTableSort({
    onPageReset: () => setPage(1),
  });
  // Per-row "NEW" tracking — clicking a transaction marks it seen and
  // decrements the menu badge by 1.
  const { markTransactionSeen, unreadTransactionIds } = useUnreadBadges();

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/transactions/?limit=1000`, { headers });
      const data = r.ok ? await r.json() : [];
      setAllRows(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => applyClientFilter(allRows, filters), [allRows, filters]);
  // Sort after filter, before pagination. Numeric/date-aware: amount sorts
  // numerically, created_at sorts chronologically (ISO strings compare
  // lexicographically the right way), text columns are case-insensitive.
  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    const map = {
      date: 'created_at',
      created_at: 'created_at',
      amount: 'amount',
      type: 'type',
      direction: 'direction',
      status: 'status',
      description: 'description',
    };
    const field = map[sortBy];
    if (!field) return rows;
    const dir = sortDir === 'desc' ? -1 : 1;
    const numeric = field === 'amount';
    const copy = [...rows];
    copy.sort((a, b) => {
      let va = a[field], vb = b[field];
      if (va == null || va === '') return 1;       // nulls last (both dirs)
      if (vb == null || vb === '') return -1;
      if (numeric) {
        return ((+va) - (+vb)) * dir;
      }
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    return copy;
  }, [rows, sortBy, sortDir]);
  const analytics = useMemo(() => computeAnalytics(rows), [rows]);
  const pageRows = useMemo(() => paginate(sortedRows, page), [sortedRows, page]);
  // Reset to first page whenever filtering changes the result count
  useEffect(() => { setPage(1); }, [filters]);

  const downloadInvoice = async (t) => {
    try {
      const r = await fetch(`${API_URL}/api/transactions/${t.id}/invoice`, { headers });
      if (!r.ok) {
        const tx = await r.text();
        throw new Error(tx || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = `invoice-${t.id.slice(0, 8)}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const clear = () => setFilters(blank);

  // Build the export query string from the active filters so the
  // downloaded XLSX / PDF matches what the user sees on screen.
  const exportQs = (() => {
    const params = new URLSearchParams();
    if (filters.date_start) params.set('date_start', filters.date_start);
    if (filters.date_end)   params.set('date_end',   filters.date_end);
    if (filters.direction)  params.set('direction',  filters.direction);
    if (filters.status)     params.set('status',     filters.status);
    if (filters.type)       params.set('type',       filters.type);
    return params.toString();
  })();

  return (
    <div className="p-4 md:p-6 w-full lg:w-[70%] mx-auto" data-testid="user-transactions-page">
      <PageHeader
        icon={<DollarSign className="w-5 h-5" />}
        title={t('page_title', { defaultValue: 'My Transactions' })}
        subtitle={t('page_subtitle', { defaultValue: 'Your recharges, order payments and refunds' })}
        testid="user-transactions-header"
        actions={
          <div className="flex gap-2 flex-wrap">
            <ExportMenu
              url={`${API_URL}/api/transactions/export-mine${exportQs ? `?${exportQs}` : ''}`}
              token={token}
              filename="my-transactions"
              testid="usrtx-export"
              label={t('export', { defaultValue: 'Export' })}
            />
            <button onClick={() => setShowCharts((s) => !s)} data-testid="toggle-charts-btn"
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all text-sm ${showCharts ? 'bg-cyan-100 text-cyan-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              <BarChart3 className="w-4 h-4" /> <span className="hidden sm:inline">{t('analytics', { defaultValue: 'Analytics' })}</span>
            </button>
          </div>
        }
      />

      {/* Stat strip — Phase-1 design refresh: replaced the 5 full-saturation
          gradient cards with calm `KpiCard` primitives. Same data, far
          less visual noise. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KpiCard
          tone="neutral"
          label={t('stat.total', { defaultValue: 'Total transactions' })}
          value={analytics.total_count.toLocaleString()}
          icon={<FileText className="w-3.5 h-3.5" />}
          testid="stat-total-count"
        />
        <KpiCard
          tone="success"
          label={t('stat.paid', { defaultValue: 'Paid (real €)' })}
          value={`€${analytics.paid_total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          icon={<DollarSign className="w-3.5 h-3.5" />}
          hint={`${analytics.paid_count} ${t('stat.txn', { defaultValue: 'txn' })}`}
          testid="stat-paid-total"
        />
        <KpiCard
          tone="warn"
          label={t('stat.bonus', { defaultValue: 'Bonus credits' })}
          value={`€${analytics.bonus_total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          icon={<DollarSign className="w-3.5 h-3.5" />}
          hint={`${analytics.bonus_count} ${t('stat.txn', { defaultValue: 'txn' })}`}
          testid="stat-bonus-total"
        />
        <KpiCard
          tone="success"
          label={t('stat.in', { defaultValue: 'In' })}
          value={`€${((analytics.by_direction.find((x) => x.key === 'to_user') || {}).amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={<ArrowDownLeft className="w-3.5 h-3.5" />}
          testid="stat-in"
        />
        <KpiCard
          tone="danger"
          label={t('stat.out', { defaultValue: 'Out' })}
          value={`€${((analytics.by_direction.find((x) => x.key === 'from_user') || {}).amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={<ArrowUpRight className="w-3.5 h-3.5" />}
          testid="stat-out"
        />
      </div>

      {/* Charts */}
      {showCharts && rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">{t('columns.status')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={analytics.by_status.map((x) => ({ name: STATUS_META[x.key]?.label || x.key, value: x.amount }))} dataKey="value" outerRadius={70} label>
                  {analytics.by_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">{t('columns.type')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.by_type.map((x) => ({ name: x.key, amount: x.amount }))} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis width={70} tickFormatter={(v) => `€${v}`} />
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Bar dataKey="amount" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-3">{t('stats.this_month')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={analytics.daily.slice(-30)} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis width={70} tickFormatter={(v) => `€${v}`} />
                <Tooltip formatter={(v) => `€${Number(v).toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="in_amount"  stroke="#10B981" strokeWidth={2} name="In"  dot={false} />
                <Line type="monotone" dataKey="out_amount" stroke="#EF4444" strokeWidth={2} name="Out" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters — shared FilterPanel pattern. Customer view is a
          simplified version of the Admin Transactions filter:
          Basic: Search, Date (+presets), Type, Direction, Status.
          Advanced: Balance type, Amount. All semantics preserved. */}
      {(() => {
        const FILTER_LABELS = {
          date_start: 'Date from', date_end: 'Date to', description: 'Search',
          direction: 'Direction', status: 'Status', type: 'Type',
          bonus_filter: 'Balance type',
        };
        const activeChips = Object.entries(filters)
          .filter(([k, v]) => v !== '' && v != null && k !== 'money_op' && k !== 'money_value' && k !== 'money_value2' && (k !== 'bonus_filter' || v !== 'all'))
          .map(([k, v]) => ({
            key: k, label: FILTER_LABELS[k] || k, value: v,
            onRemove: () => setFilters((p) => ({ ...p, [k]: k === 'bonus_filter' ? 'all' : '' })),
          }));
        if (filters.money_op && filters.money_value !== '') {
          const opSym = { eq: '=', gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between' }[filters.money_op] || filters.money_op;
          const v = filters.money_op === 'between' && filters.money_value2 !== ''
            ? `${filters.money_value} – ${filters.money_value2}` : `${opSym} ${filters.money_value}`;
          activeChips.push({ key: 'money', label: 'Amount', value: v,
            onRemove: () => setFilters((p) => ({ ...p, money_op: '', money_value: '', money_value2: '' })) });
        }
        const advancedActiveCount = (filters.bonus_filter && filters.bonus_filter !== 'all' ? 1 : 0)
          + (filters.money_op && filters.money_value !== '' ? 1 : 0);
        return (
          <FilterPanel
            open={showFilters}
            onToggle={() => setShowFilters((s) => !s)}
            activeChips={activeChips}
            onClearAll={clear}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((s) => !s)}
            advancedActiveCount={advancedActiveCount}
            testid="usrtx-filters"
            labels={{
              filters: t('filters_label', { defaultValue: 'Filters' }),
              hide: t('hide', { defaultValue: 'Hide' }),
              show: t('show', { defaultValue: 'Show' }),
              clear: t('clear', { defaultValue: 'Clear' }),
              clear_all: t('clear', { defaultValue: 'Clear all' }),
              advanced_filters: t('advanced_filters', { defaultValue: 'Advanced filters' }),
              hide_filters: t('hide', { defaultValue: 'Hide filters' }),
              show_filters: t('show', { defaultValue: 'Show filters' }),
            }}
            advancedChildren={
              <>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Balance type</label>
                  <select value={filters.bonus_filter} onChange={(e) => setFilters({ ...filters, bonus_filter: e.target.value })} data-testid="usrtx-bonus" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="all">All</option>
                    <option value="hide">Real payments only</option>
                    <option value="only">{t('bonus_only_option', { defaultValue: 'Bonus credits only' })}</option>
                  </select>
                </div>
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide flex items-center gap-1"><DollarSign className="w-3 h-3" /> Amount</label>
                  <div className="flex flex-wrap gap-1.5">
                    <select value={filters.money_op} onChange={(e) => setFilters({ ...filters, money_op: e.target.value })} data-testid="usrtx-money-op" className="h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                      <option value="">— No filter —</option>
                      <option value="eq">= equal to</option>
                      <option value="gt">&gt; greater than</option>
                      <option value="gte">≥ at least</option>
                      <option value="lt">&lt; less than</option>
                      <option value="lte">≤ at most</option>
                      <option value="between">between</option>
                    </select>
                    <input type="number" step="any" value={filters.money_value} onChange={(e) => setFilters({ ...filters, money_value: e.target.value })} placeholder="€ amount" className="h-9 px-2 text-xs rounded-md border border-slate-200 w-28" />
                    {filters.money_op === 'between' && (
                      <input type="number" step="any" value={filters.money_value2} onChange={(e) => setFilters({ ...filters, money_value2: e.target.value })} placeholder="€ to" className="h-9 px-2 text-xs rounded-md border border-slate-200 w-28" />
                    )}
                  </div>
                </div>
              </>
            }
          >
            <div className="col-span-1 sm:col-span-2">
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tCommon('buttons.search')}</label>
              <input type="text" value={filters.description} onChange={(e) => setFilters({ ...filters, description: e.target.value })} placeholder={t('description_or_id', { defaultValue: 'Description or transaction ID' })} data-testid="usrtx-description" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" />
            </div>
            <DateRangeFilter
              from={filters.date_start}
              to={filters.date_end}
              onChange={({ from, to }) => setFilters({ ...filters, date_start: from, date_end: to })}
              testidPrefix="usrtx-date"
              label={t('date_label', { defaultValue: 'Date' })}
              presetLabels={{
                today: t('presets.today', { defaultValue: 'Today' }),
                d7: t('presets.d7', { defaultValue: '7 days' }),
                d30: t('presets.d30', { defaultValue: '30 days' }),
                this_month: t('presets.this_month', { defaultValue: 'This month' }),
              }}
            />
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{t('filters.type', { defaultValue: 'Type' })}</label>
              <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="usrtx-type" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                <option value="">{t('filters.any_type', { defaultValue: 'Any type' })}</option>
                <option value="recharge">{t('types.recharge', { defaultValue: 'Recharge' })}</option>
                <option value="order_reserve">{t('types.order_reserve', { defaultValue: 'Order reserve' })}</option>
                <option value="order_complete">{t('types.order_complete', { defaultValue: 'Order complete' })}</option>
                <option value="order_refund">{t('types.order_refund', { defaultValue: 'Order refund' })}</option>
                <option value="admin_adjustment">{t('types.admin_adjustment', { defaultValue: 'Adjustment / Bonus' })}</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{t('filters.direction', { defaultValue: 'Direction' })}</label>
              <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} data-testid="usrtx-direction" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                <option value="">{t('filters.any_direction', { defaultValue: 'Any direction' })}</option>
                <option value="to_user">{t('directions.to_user', { defaultValue: 'In (top-up)' })}</option>
                <option value="from_user">{t('directions.from_user', { defaultValue: 'Out (paid)' })}</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{t('filters.status', { defaultValue: 'Status' })}</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="usrtx-status" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                <option value="">{t('filters.any_status', { defaultValue: 'Any status' })}</option>
                <option value="pending">{t('status.pending', { defaultValue: 'Pending' })}</option>
                <option value="tested">{t('status.locked', { defaultValue: 'Locked' })}</option>
                <option value="end">{t('status.end', { defaultValue: 'End' })}</option>
                <option value="completed">{t('status.completed', { defaultValue: 'Completed' })}</option>
                <option value="failed">{t('status.failed', { defaultValue: 'Failed' })}</option>
              </select>
            </div>
          </FilterPanel>
        );
      })()}

      {/* Table — desktop / tablet only (mobile renders card stack below).
          Header uses grey bg + dark text and rows zebra-stripe — matches
          the look of the legacy fs.auto-remap.com Yii1 admin so customers
          migrating from the old site recognise the layout instantly. */}
      <div className="hidden md:block">
        <div className="overflow-hidden bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="overflow-x-auto max-h-[calc(100vh-360px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-200 border-b border-slate-300">
                <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  <SortableHeader label={t('columns.date', { defaultValue: 'Date' })} field="date" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                  <SortableHeader label={t('columns.type', { defaultValue: 'Type' })} field="type" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                  <SortableHeader label={t('columns.direction', { defaultValue: 'Direction' })} field="direction" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                  <SortableHeader label={t('columns.amount', { defaultValue: 'Amount' })} field="amount" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="right" className="hover:bg-slate-300/50" />
                  <SortableHeader label={t('columns.status', { defaultValue: 'Status' })} field="status" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                  <SortableHeader label={t('columns.description', { defaultValue: 'Description' })} field="description" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                  <th className="px-2.5 py-3 text-center whitespace-nowrap">{t('columns.invoice', { defaultValue: 'Invoice' })}</th>
                </tr>
              </thead>
              <tbody data-testid="usrtx-tbody">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />{t('loading', { defaultValue: 'Loading…' })}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">{t('no_match_filters', { defaultValue: 'No transactions match the filters.' })}</td></tr>
                ) : (
                  pageRows.map((tx, idx) => {
                    const dm = DIR_META[tx.direction] || { label: tx.direction, cls: '', icon: ArrowUpRight };
                    const DIcon = dm.icon;
                    const canInvoice = tx.type === 'recharge' && !tx.is_bonus;
                    const isNew = unreadTransactionIds && unreadTransactionIds.has(tx.id);
                    const sm = STATUS_META[tx.status] || STATUS_META.pending;
                    const SIcon = sm.icon;
                    // Row tint priority: NEW (amber + left border) > bonus
                    // (amber soft) > zebra (white / slate-50). Hover always
                    // adds a faint cyan tint so the user knows which row
                    // they're hovering. Zebra is the legacy fs.auto-remap.com
                    // look — restored by user request 08/Jun/2026.
                    const zebra = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                    const rowBg = isNew
                      ? 'bg-amber-50/70 border-l-4 border-l-amber-400'
                      : tx.is_bonus ? 'bg-amber-50/30' : zebra;
                    return (
                      <tr
                        key={tx.id}
                        onClick={() => isNew && markTransactionSeen(tx.id)}
                        className={`border-b border-slate-100 cursor-pointer hover:bg-cyan-50/60 transition-colors ${rowBg}`}
                        data-testid={`usrtx-row-${tx.id}`}
                      >
                        <td className="px-2.5 py-2.5 text-[12px] whitespace-nowrap font-mono-ui text-slate-500 tabular-nums">
                          {fmtDate(tx.created_at)}
                          {isNew && (
                            <span className="ml-2 inline-flex items-center justify-center px-1.5 h-[16px] bg-amber-500 text-white text-[9px] font-extrabold rounded uppercase tracking-wider align-middle" data-testid={`usrtx-new-${tx.id}`}>NEW</span>
                          )}
                        </td>
                        <td className="px-2.5 py-2.5 text-xs whitespace-nowrap">
                          <StatusBadge tone="neutral" size="sm">{t(`types.${tx.type}`, { defaultValue: tx.type })}</StatusBadge>
                          {tx.is_bonus && <StatusBadge tone="warn" size="sm" className="ml-1">{t('bonus_badge', { defaultValue: 'BONUS' })}</StatusBadge>}
                        </td>
                        <td className={`px-2.5 py-2.5 text-xs whitespace-nowrap ${dm.cls}`}><div className="inline-flex items-center gap-1 font-medium"><DIcon className="w-3.5 h-3.5" />{t(`directions.${tx.direction}_short`, { defaultValue: dm.label })}</div></td>
                        <td className={`px-2.5 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap text-[13px] ${tx.direction === 'to_user' ? (tx.is_bonus ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>{tx.direction === 'to_user' ? '+' : '−'}€{Number(tx.amount || 0).toFixed(2)}</td>
                        <td className="px-2.5 py-2.5 whitespace-nowrap">
                          <StatusBadge tone={toneForStatus(tx.status === 'tested' ? 'pending' : tx.status === 'end' ? 'completed' : tx.status)} size="sm" dot>
                            <SIcon className="w-3 h-3" /> {t(`status.${tx.status}`, { defaultValue: sm.label })}
                          </StatusBadge>
                        </td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-600 max-w-[260px]"><DescriptionWithOrderLink tx={tx} onOpenOrder={onOpenOrder} tT={t} /></td>
                        <td className="px-2.5 py-2.5 text-center whitespace-nowrap">
                          {canInvoice ? (
                            <button onClick={(e) => { e.stopPropagation(); markTransactionSeen(tx.id); downloadInvoice(tx); }} title="Download invoice" data-testid={`usrtx-invoice-${tx.id}`}
                              className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded inline-flex items-center justify-center"><FileText className="w-4 h-4" /></button>
                          ) : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <Pagination page={page} total={rows.length} pageSize={PAGE_SIZE} onChange={setPage} label="transactions" />
          )}
        </div>
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3" data-testid="usrtx-mobile-cards">
        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> {t('loading', { defaultValue: 'Loading…' })}
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            {t('no_match_filters', { defaultValue: 'No transactions match the filters.' })}
          </div>
        )}
        {!loading && pageRows.map((tx) => {
          const sm = STATUS_META[tx.status] || STATUS_META.pending;
          const dm = DIR_META[tx.direction] || { label: tx.direction, cls: '', icon: ArrowUpRight };
          const SIcon = sm.icon; const DIcon = dm.icon;
          const canInvoice = tx.type === 'recharge' && !tx.is_bonus;
          const isNew = unreadTransactionIds && unreadTransactionIds.has(tx.id);
          return (
            <div
              key={tx.id}
              data-testid={`usrtx-card-${tx.id}`}
              onClick={() => isNew && markTransactionSeen(tx.id)}
              className={`bg-white rounded-xl border ${isNew ? 'border-amber-300' : (tx.is_bonus ? 'border-amber-200' : 'border-slate-200')} shadow-sm overflow-hidden ${isNew ? 'cursor-pointer' : ''}`}
            >
              <div className="px-4 py-3 flex items-center justify-between gap-2 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{fmtDate(tx.created_at)}</p>
                  <div className="flex items-center gap-1 flex-wrap mt-0.5">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">{t(`types.${tx.type}`, { defaultValue: tx.type })}</span>
                    {tx.is_bonus && <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold uppercase tracking-wide">{t('bonus_badge', { defaultValue: 'BONUS' })}</span>}
                    {isNew && <span className="px-2 py-0.5 bg-amber-500 text-white rounded text-[10px] font-extrabold uppercase tracking-wider" data-testid={`usrtx-new-mob-${tx.id}`}>NEW</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-lg font-bold ${tx.direction === 'to_user' ? (tx.is_bonus ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>
                    {tx.direction === 'to_user' ? '+' : '−'}€{Number(tx.amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <dl className="px-4 py-2.5 text-sm divide-y divide-slate-100">
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{t('filters.direction', { defaultValue: 'Direction' })}</dt>
                  <dd className={`inline-flex items-center gap-1 font-medium ${dm.cls}`}>
                    <DIcon className="w-3.5 h-3.5" /> {t(`directions.${tx.direction}_short`, { defaultValue: dm.label })}
                  </dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{t('columns.status', { defaultValue: 'Status' })}</dt>
                  <dd>
                    <span className={`px-2 py-1 rounded border text-[10px] font-bold inline-flex items-center gap-1 ${sm.cls}`}>
                      <SIcon className="w-3 h-3" />{t(`status.${tx.status}`, { defaultValue: sm.label })}
                    </span>
                  </dd>
                </div>
                {tx.description && (
                  <div className="py-1.5">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">{t('columns.description', { defaultValue: 'Description' })}</dt>
                    <dd className="text-slate-700 text-[13px] leading-tight break-words"><DescriptionWithOrderLink tx={tx} onOpenOrder={onOpenOrder} tT={t} /></dd>
                  </div>
                )}
              </dl>
              {canInvoice && (
                <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50">
                  <button
                    onClick={(e) => { e.stopPropagation(); markTransactionSeen(tx.id); downloadInvoice(tx); }}
                    data-testid={`usrtx-invoice-mob-${tx.id}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all"
                  >
                    <FileText className="w-4 h-4" /> {t('download_invoice', { defaultValue: 'Download invoice' })}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {rows.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <Pagination page={page} total={rows.length} pageSize={PAGE_SIZE} onChange={setPage} label="transactions" />
          </div>
        )}
      </div>
    </div>
  );
}
